import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-work-order-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1400px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <a routerLink="/admin/financials/work-orders" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Work Orders
            </a>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">{{ form.work_order_no || (isExistingOrderRoute ? 'Loading work order' : 'New Work Order') }}</h1>
            <p class="text-slate-500 text-xs mt-1">Capture the recipient, dates, property details, contacts, tenants, and matching PDF output.</p>
          </div>
          <div class="flex items-center gap-2 flex-wrap justify-end">
            @if (form.id && !isLoadingOrder()) {
              <button type="button" (click)="openPdf()" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2"><i class="ti ti-file-type-pdf"></i>Preview PDF</button>
              <button type="button" (click)="sendEmail()" [disabled]="isSending() || !form.to_email" class="h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-2"><i class="ti" [class]="isSending() ? 'ti-loader animate-spin' : 'ti-mail-forward'"></i>{{ isSending() ? 'Sending...' : (form.emailed_at ? 'Send Again' : 'Email Customer') }}</button>
            }
            <button type="button" (click)="save()" [disabled]="isSaving() || isLoadingOrder()" class="h-9 px-4 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] disabled:opacity-50 flex items-center gap-2">
              <i class="ti" [class]="isSaving() ? 'ti-loader animate-spin' : 'ti-device-floppy'"></i>Save
            </button>
          </div>
        </header>

        @if (isLoadingOrder()) {
          <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-8 text-center">
            <div class="mx-auto w-11 h-11 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center">
              <i class="ti ti-loader animate-spin text-2xl"></i>
            </div>
            <h2 class="mt-3 text-base font-bold text-slate-900">Loading work order</h2>
            <p class="mt-1 text-sm text-slate-500">The existing work order details are being loaded.</p>
          </section>
        } @else {
          <div class="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
            <div class="space-y-5">
              <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 space-y-4">
                <div class="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <p class="text-sm font-bold text-slate-900">Work Order Details</p>
                    <p class="text-[11px] text-slate-500">These fields drive the PDF header and summary block.</p>
                  </div>
                  <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="statusBadgeClass(form.status)">{{ form.status }}</span>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <label class="md:col-span-2 text-xs font-semibold text-slate-600">To <span class="text-rose-500">*</span>
                    <input [(ngModel)]="form.to_name" placeholder="rubal sethi" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </label>
                  <label class="md:col-span-2 text-xs font-semibold text-slate-600">Recipient email
                    <input [(ngModel)]="form.to_email" type="email" placeholder="customer@example.com" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </label>
                  <label class="text-xs font-semibold text-slate-600">Created date <span class="text-rose-500">*</span>
                    <input [(ngModel)]="form.created_date" type="date" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </label>
                  <label class="text-xs font-semibold text-slate-600">Work due date
                    <input [(ngModel)]="form.work_due_date" type="date" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </label>
                  <label class="text-xs font-semibold text-slate-600">Reference No.
                    <input [(ngModel)]="form.reference_no" placeholder="177" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </label>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label class="text-xs font-semibold text-slate-600">Priority <span class="text-rose-500">*</span>
                    <select [(ngModel)]="form.priority" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs bg-white">
                      @for (priority of priorities; track priority) { <option [value]="priority">{{ priority }}</option> }
                    </select>
                  </label>
                  <label class="text-xs font-semibold text-slate-600">Status <span class="text-rose-500">*</span>
                    <select [(ngModel)]="form.status" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs bg-white">
                      @for (status of statuses; track status) { <option [value]="status">{{ status }}</option> }
                    </select>
                  </label>
                  <label class="text-xs font-semibold text-slate-600">Property name
                    <input [(ngModel)]="form.property_name" placeholder="123 test" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </label>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <textarea [(ngModel)]="form.property_address" rows="3" placeholder="Property address" class="rounded-xl border border-slate-200 px-3 py-2 text-xs"></textarea>
                  <textarea [(ngModel)]="form.description" rows="3" placeholder="Work description" class="rounded-xl border border-slate-200 px-3 py-2 text-xs"></textarea>
                </div>
              </section>

              <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 space-y-4">
                <div class="border-b border-slate-100 pb-3">
                  <p class="text-sm font-bold text-slate-900">Contacts</p>
                  <p class="text-[11px] text-slate-500">Mirrors the three-column contact block in the work order PDF.</p>
                </div>
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <p class="text-xs font-bold text-slate-900">For access contact</p>
                    <input [(ngModel)]="form.access_contact_name" placeholder="Name" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.access_contact_phone" placeholder="Phone" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.access_contact_email" placeholder="Email" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </div>
                  <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <p class="text-xs font-bold text-slate-900">For queries contact</p>
                    <input [(ngModel)]="form.queries_contact_name" placeholder="Name" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.queries_contact_phone" placeholder="Phone" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.queries_contact_email" placeholder="Email" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </div>
                  <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <p class="text-xs font-bold text-slate-900">Send invoices to</p>
                    <input [(ngModel)]="form.invoice_name" placeholder="Name" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.invoice_email" placeholder="Email" class="w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <textarea [(ngModel)]="form.invoice_address" rows="2" placeholder="Address" class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea>
                  </div>
                </div>
              </section>

              <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p class="text-sm font-bold text-slate-900 mb-3">Tenants</p>
                  <div class="grid grid-cols-2 gap-2">
                    <input [(ngModel)]="form.tenant_one_name" placeholder="Tenant name" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.tenant_one_phone" placeholder="Phone" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.tenant_two_name" placeholder="Tenant name" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                    <input [(ngModel)]="form.tenant_two_phone" placeholder="Phone" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  </div>
                </div>
                <div>
                  <p class="text-sm font-bold text-slate-900 mb-3">Notes</p>
                  <textarea [(ngModel)]="form.notes" rows="4" placeholder="Internal or PDF notes" class="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs"></textarea>
                </div>
              </section>
            </div>

            <aside class="space-y-4">
              <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 sticky top-4">
                <p class="text-sm font-bold text-slate-900">Summary</p>
                <div class="mt-4 space-y-2">
                  <div class="flex items-center justify-between text-xs text-slate-500"><span>Recipient</span><strong class="text-slate-900 text-right">{{ form.to_name || '-' }}</strong></div>
                  <div class="flex items-center justify-between text-xs text-slate-500"><span>Email</span><strong class="text-slate-900 text-right">{{ form.to_email || '-' }}</strong></div>
                  @if (form.emailed_at) { <div class="flex items-center justify-between text-xs text-slate-500"><span>Last sent</span><strong class="text-emerald-700 text-right">{{ form.emailed_at | date:'dd MMM yyyy, h:mm a' }}</strong></div> }
                  <div class="flex items-center justify-between text-xs text-slate-500"><span>Reference</span><strong class="text-slate-900">{{ form.reference_no || '-' }}</strong></div>
                  <div class="flex items-center justify-between text-xs text-slate-500"><span>Due</span><strong class="text-slate-900">{{ form.work_due_date ? (form.work_due_date | date:'dd MMM yyyy') : 'As agreed' }}</strong></div>
                  <div class="flex items-center justify-between text-sm text-slate-900 py-3 border-t border-slate-200 mt-2">
                    <span class="font-bold">Priority</span>
                    <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="priorityBadgeClass(form.priority)">{{ form.priority }}</span>
                  </div>
                </div>
              </section>
            </aside>
          </div>
        }
      </div>
    </div>
  `
})
export class AdminWorkOrderDetailComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  isSaving = signal(false);
  isSending = signal(false);
  isLoadingOrder = signal(false);
  statuses = ['Draft', 'Issued', 'In Progress', 'Completed', 'Cancelled'];
  priorities = ['Low', 'Normal', 'Urgent'];
  form: any = this.emptyForm();
  isExistingOrderRoute = false;

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.isExistingOrderRoute = true;
      this.loadWorkOrder(id);
    }
  }

  emptyForm() {
    return {
      id: null,
      work_order_no: '',
      to_name: '',
      to_email: '',
      created_date: new Date().toISOString().slice(0, 10),
      work_due_date: '',
      reference_no: '',
      priority: 'Urgent',
      status: 'Draft',
      property_name: '',
      property_address: '',
      description: '',
      access_contact_name: '',
      access_contact_phone: '',
      access_contact_email: '',
      queries_contact_name: '',
      queries_contact_phone: '',
      queries_contact_email: '',
      invoice_name: '',
      invoice_email: '',
      invoice_address: '',
      tenant_one_name: '',
      tenant_one_phone: '',
      tenant_two_name: '',
      tenant_two_phone: '',
      notes: ''
    };
  }

  loadWorkOrder(id: string) {
    this.isLoadingOrder.set(true);
    this.api.getWorkOrder(id).subscribe({
      next: (res: any) => {
        this.form = { ...this.emptyForm(), ...(res.data?.work_order || {}) };
        this.isLoadingOrder.set(false);
      },
      error: () => {
        this.isLoadingOrder.set(false);
        showToast('error', 'Could not load work order.');
      }
    });
  }

  save() {
    if (!this.form.to_name?.trim()) return showToast('error', 'To name is required.');
    this.isSaving.set(true);
    this.api.saveWorkOrder(this.form).subscribe({
      next: (res: any) => {
        showToast('success', 'Work order saved.');
        this.isSaving.set(false);
        if (!this.form.id) this.router.navigate(['/admin/financials/work-orders', res.data.id]);
        this.loadWorkOrder(res.data.id);
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err.error?.message || 'Could not save work order.');
      }
    });
  }

  openPdf() {
    if (!this.form.id) return;
    this.router.navigate(['/admin/financials/pdf', 'work-order', this.form.id]);
  }

  sendEmail() {
    if (!this.form.id) return;
    if (!this.form.to_email?.trim()) return showToast('error', 'Recipient email is required.');
    this.isSending.set(true);
    this.api.saveWorkOrder(this.form).subscribe({
      next: (saveRes: any) => {
        const id = saveRes.data?.id || this.form.id;
        this.api.sendWorkOrder(id).subscribe({
          next: () => {
            this.isSending.set(false);
            showToast('success', `Work order emailed to ${this.form.to_email}.`);
            this.loadWorkOrder(String(id));
          },
          error: (err) => {
            this.isSending.set(false);
            showToast('error', err.error?.message || 'Could not send work order.');
          }
        });
      },
      error: (err) => {
        this.isSending.set(false);
        showToast('error', err.error?.message || 'Could not save work order before sending.');
      }
    });
  }

  priorityBadgeClass(priority: string): string {
    if (priority === 'Urgent') return 'text-amber-700 bg-amber-50 border-amber-100';
    if (priority === 'Low') return 'text-slate-600 bg-slate-50 border-slate-200';
    return 'text-sky-700 bg-sky-50 border-sky-100';
  }

  statusBadgeClass(status: string): string {
    if (status === 'Completed') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Issued' || status === 'In Progress') return 'text-sky-700 bg-sky-50 border-sky-100';
    if (status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}
